/**
 * B76C · screens 2c–2d — ONBOARDING-LITE: the step list a coach account runs
 * after buying the athlete membership.
 *
 * Same posture as `onboardingCtaCopy.ts` (B45) and `onboardingAgeCopy.ts`
 * (REL-29): no React, no router, no CSS in the graph, so the rule and the
 * strings are readable and pinnable on their own.
 *
 * ── THE RULE ──────────────────────────────────────────────────────────────
 * A coach-first account has answered NOTHING about its own running. It has
 * also never been asked its age by the athlete wizard. So the lite flow is:
 *
 *   1. the AGE step, always, first, and never skippable
 *   2. then only the questions this account has not already answered
 *
 * ⛔ WHY AGE IS UNCONDITIONAL. The CD handoff is explicit: "No upgrade path may
 * skip this step." Age is not decoration in this product — it drives the
 * clinical bands, the 13-15 guardian beat, the minor-commerce refusal and the
 * COPPA hold, and REL-29 exists precisely because age used to arrive
 * incidentally. An upgrade path that inherited an age from somewhere else
 * would be a second, quieter way for an account to reach the athlete side
 * unaged. An adult coach who has answered before sees their own values
 * prefilled and moves on in one tap; that is the cost, and it is the right
 * one. The step itself is the EXISTING `<AgeStep>` with its FROZEN strings.
 *
 * ── WHICH QUESTIONS ───────────────────────────────────────────────────────
 * Exactly the three the handoff enumerates, in the order its chapter card
 * lists them: the journey, how you pace yourself (skippable), and body basics
 * (which is the PAR-Q step plus the body-metrics step). Location and focus are
 * NOT in the reduced list — this is a reduction of the ratified list, not a
 * re-derivation of it, and widening it is a design decision rather than an
 * implementation one.
 *
 * ── WHAT IT DOES NOT DECIDE ───────────────────────────────────────────────
 * CTA labels. B45's lesson was that "which screen is last" is a property of
 * the flow and no step may claim it, so the labels here are still resolved
 * through `onboardingCtaLabel` — this module only maps a lite step onto the
 * screen name that function already knows. No screen is added to B45's union,
 * and none of its logic changes.
 *
 * 🏷️ The 2d chapter strings are the ratified CD copy. The "Chapter 1 of 2"
 * kicker is a PLACEHOLDER: the chapter names and count defer to the
 * onboarding-architecture chapter map (`.claude/briefs/cd-brief-onboarding-
 * architecture.md`), which lands later.
 */

import type { OnboardingAnswers } from '../hooks/onboardingReducer';
import type { OnboardingCtaScreen } from './onboardingCtaCopy';
import { parqAnswersComplete } from './parqAnswers';

/** The screens the lite flow can run, in flow order. */
export type LiteStepId = 'age' | 'journey' | 'pace' | 'parq' | 'body';

/** Flow order. `age` leads; the rest follow the 2d chapter card's order. */
export const LITE_STEP_ORDER: readonly LiteStepId[] = [
  'age',
  'journey',
  'pace',
  'parq',
  'body',
];

/** The steps an athlete may pass without answering. Only pace, per 2d. */
export const LITE_SKIPPABLE: readonly LiteStepId[] = ['pace'];

/** Ratified 2d copy (CD handoff, screen 02d-onboarding-lite-chapter). */
export const ONBOARDING_LITE_CHAPTER_COPY = {
  /** ⚠️ PLACEHOLDER — see the file header. */
  kicker: 'Chapter 1 of 2',
  headline: 'Getting to know you as an athlete.',
  body: 'Tayro already knows you as a coach. A few questions about your own running set up your plan.',
  rowJourney: 'Your journey',
  rowPace: 'How you pace yourself',
  rowBody: 'Body basics',
  cta: 'Continue',
} as const;

/**
 * Has this account already answered the question behind a lite step?
 *
 * Reads the wizard answer bag, which is the same bag the full wizard writes and
 * `usePersistOnboarding` projects — so a lite run that is interrupted and
 * resumed re-derives the same remaining list from the rehydrated blob.
 *
 * `age` is never "answered" for the purposes of the queue: see the header.
 */
export function isLiteStepAnswered(step: LiteStepId, answers: OnboardingAnswers): boolean {
  switch (step) {
    case 'age':
      // Deliberately always false. The age step is unconditional, and this is
      // the single place that decision is expressed.
      return false;
    case 'journey':
      return !!answers.journey;
    case 'pace':
      // Either branch counts, and an explicit skip counts too: the athlete was
      // asked and answered "not now", which is an answer.
      return (
        typeof answers.raceTimeSeconds === 'number' ||
        answers.skipPace === true ||
        !!answers.qualPace
      );
    case 'parq':
      // ⚖️ B108 — the SAME predicate the completion payload gates its
      // `_parq_completed` stamp on, and for the same reason. This used to read
      // "the bag has at least one key", so a coach whose blob carried one
      // stray or camelCase key SKIPPED the screening step and then crossed to
      // the athlete side with an incomplete bag. The two rules have to be one
      // rule: a step this flow skips is a question the payload treats as
      // answered, and a clinical answer is an explicit boolean on a canonical
      // key or it does not exist.
      return parqAnswersComplete(answers.parq);
    case 'body':
      return [
        answers.weightLbs,
        answers.weightKg,
        answers.heightFt,
        answers.heightIn,
        answers.heightCm,
      ].some((v) => typeof v === 'string' && v.trim() !== '');
    default: {
      // Exhaustiveness guard: a NEW step added to the union without a case
      // here fails the type-check rather than silently queueing forever.
      const never: never = step;
      return never;
    }
  }
}

/**
 * The ordered list of lite steps this account still has to run.
 *
 * Always non-empty and always starts with `age`.
 */
export function deriveOnboardingLiteSteps(answers: OnboardingAnswers): LiteStepId[] {
  return LITE_STEP_ORDER.filter((step) => !isLiteStepAnswered(step, answers));
}

/**
 * The B45 screen name a lite step wears, or null for the age step — which owns
 * its own ratified copy in `onboardingAgeCopy.ts` and is deliberately absent
 * from B45's union because it can never be last.
 *
 * This mapping is the whole of what the lite flow says about CTA labels. It
 * adds no screen to B45's union and changes none of its logic; every lite step
 * lands on a screen B45 already classifies as never-last, so the lite flow can
 * never announce the coaches reveal — which is correct, because this flow ends
 * at the arrival banner (2e), not at the reveal. The invariant is pinned in
 * `onboardingLite.b76c.test.ts` against `onboardingCtaLabel` itself, so a
 * future step added here without a case fails the type-check, and a future
 * change to B45's rule fails the test.
 */
export function liteStepCtaScreen(step: LiteStepId): OnboardingCtaScreen | null {
  switch (step) {
    case 'age':
      return null;
    case 'journey':
      return 'step1_journey';
    case 'pace':
      return 'step5_pace';
    case 'parq':
      return 'step3_parq';
    case 'body':
      return 'step4_body_metrics';
    default: {
      const never: never = step;
      return never;
    }
  }
}
